const { EVENTS, errorReason, errorOutcome, aiOutcome, requestContext } = require('./productInsights');

/** Dependency injection keeps the browser observer offline-testable without an analytics SDK. */
function createProductInsights({ getAnalyticsConsentRevision, isAnalyticsActive, trackEvent }) {
    function insight(event, params = {}) {
        try { trackEvent(event, { ...params, page_title: 'Architect-IQ' }); } catch { /* Analytics must not break product behavior. */ }
    }
    async function observeProductRequest(url, body, run, stream = false) {
        const context = requestContext(url, body);
        if (!context) return run();
        const consentAtStart = isAnalyticsActive();
        const consentRevision = getAnalyticsConsentRevision();
        const started = Date.now();
        const emit = (event, extra = {}) => {
            if (consentAtStart && isAnalyticsActive() && consentRevision === getAnalyticsConsentRevision()) {
                insight(event, { ...context, ...extra });
            }
        };
        if (context.kind === 'ai') emit(EVENTS.AI_STARTED);
        const finish = (outcome, reason) => emit(
            context.kind === 'ai' ? EVENTS.AI_FINISHED : EVENTS.CHECKOUT_FAILED,
            { outcome, reason, duration_ms: Date.now() - started },
        );
        try {
            const result = await run();
            if (context.kind === 'checkout') {
                if (result?.url) emit(EVENTS.CHECKOUT_READY);
                else finish('error', 'missing_checkout_url');
            } else if (stream && result instanceof Response) {
                return observeAiStream(result, finish);
            } else finish(aiOutcome(result));
            return result;
        } catch (error) {
            finish(errorOutcome(error), errorReason(error));
            throw error;
        }
    }
    return { insight, observeProductRequest };
}

/** Read-through SSE: no tee, retained answer or success at HTTP headers. */
function observeAiStream(response, finish) {
    if (!response.body) { finish('error', 'empty_stream'); return response; }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let terminal = false;
    const end = (outcome, reason) => {
        if (!terminal) { terminal = true; finish(outcome, reason); }
    };
    const body = new ReadableStream({
        async pull(controller) {
            try {
                const chunk = await reader.read();
                if (chunk.done) { end('error', 'incomplete_stream'); controller.close(); return; }
                buffer += decoder.decode(chunk.value, { stream: true });
                buffer = buffer.replace(/\r\n/g, '\n');
                let boundary;
                while ((boundary = buffer.indexOf('\n\n')) >= 0) {
                    const frame = buffer.slice(0, boundary);
                    buffer = buffer.slice(boundary + 2);
                    if (/^event: error$/m.test(frame)) end('error', 'stream_error');
                    if (/^event: done$/m.test(frame)) {
                        try {
                            const data = JSON.parse(frame.split('\n').filter(line => line.startsWith('data:')).map(line => line.slice(5).trim()).join('\n'));
                            end(aiOutcome(data));
                        } catch { end('error', 'invalid_stream'); }
                    }
                }
                if (buffer.length > 65536) { buffer = ''; end('error', 'invalid_stream'); }
                controller.enqueue(chunk.value);
            } catch (error) { end('error', 'network'); controller.error(error); }
        },
        async cancel(reason) { end('cancelled', 'cancelled'); await reader.cancel(reason); },
    });
    return new Response(body, { status: response.status, statusText: response.statusText, headers: response.headers });
}
module.exports = { createProductInsights };
